import { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import NoData from './NoData';
import ReportHeaderSection from './ReportHeaderSection';
import ReportSection from './ReportSection';
import { getSweepsData, getSweepsDataInternal } from '../../api/external-reports';
import Loader, { Types } from '../gdk/Loader';
import { sweepsFilters } from './ReportsHelper';

function getActiveFilterKeys(filters) {
    return Object.keys(filters).filter(key => {
        if (key === 'startDate' || key === 'endDate') {
            return !!filters[key];
        }
        return Array.isArray(filters[key]) && filters[key].length > 0;
    });
}

// Top-level for testing
export function filterRow(row, filters) {
    return getActiveFilterKeys(filters).every(key => {
        switch (key) {
            case 'startDate':
            case 'endDate': {
                if (!row.PaymentAppliedDate) {
                    return false;
                }
                const paymentDate = new Date(row.PaymentAppliedDate);
                if (key === 'startDate') {
                    return paymentDate >= new Date(filters[key]);
                }
                return paymentDate <= new Date(filters[key]);
            }
            case 'NSF':
                return filters[key].includes(row.InsufficientFunds ? 'Yes' : 'No');
            default: {
                const rowKey = sweepsFilters.find(filter => filter.value === key)?.value;
                return filters[key].includes(row[rowKey]);
            }
        }
    });
}

export function getSweepsDateBounds(data, fallbackStart, fallbackEnd) {
    if (!data?.length) {
        return { startDate: fallbackStart, endDate: fallbackEnd };
    }

    const paymentDates = data
        .map(row => row.PaymentAppliedDate)
        .filter(Boolean)
        .map(date => new Date(date).getTime())
        .filter(time => !Number.isNaN(time));

    if (!paymentDates.length) {
        return { startDate: fallbackStart, endDate: fallbackEnd };
    }

    return {
        startDate: new Date(Math.min(...paymentDates)),
        endDate: new Date(Math.max(...paymentDates)),
    };
}

export default function ReportSweepsPage({
    user,
    substituteAgentInfo,
    data,
    setData,
    dataLastUpdatedDate,
    dataStartDate,
    filterOptions,
    selectedDateRange,
    setSelectedDateRange = () => { },
    appliedFilters = {},
    setAppliedFilters = () => { },
}) {
    const [unfilteredData, setUnfilteredData] = useState(data);
    const [loading, setLoading] = useState(false);

    const sweepsDateBounds = useMemo(
        () => getSweepsDateBounds(unfilteredData, dataStartDate, dataLastUpdatedDate),
        [unfilteredData, dataStartDate, dataLastUpdatedDate]
    );

    const loadData = useCallback(async (selectedFilters = {}) => {
        setLoading(true);
        const handleSuccess = (response) => {
            if (response?.Status === 3) {
                setData(response?.Domain);
                if (Object.values(selectedFilters).every(value => value === null || value.length === 0)) {
                    setUnfilteredData(response?.Domain);
                }
            } else {
                setData(null);
            }

            setAppliedFilters(selectedFilters);
            setLoading(false);
        }

        if (substituteAgentInfo?.id) {
            const substituteAgentRequest = {
                substituteAgentId: substituteAgentInfo.id,
            };
            getSweepsDataInternal(substituteAgentRequest, response => handleSuccess(response))
        } else {
            getSweepsData({}, response => handleSuccess(response))
        }
    }, [substituteAgentInfo?.id, setData, setAppliedFilters]);

    useEffect(() => {
        if (!unfilteredData) {
            loadData();
        }
    }, [unfilteredData, loadData]);

    async function applyFilters(filters) {
        setLoading(true);
        setAppliedFilters(filters);
        if (unfilteredData) {
            const filteredData = getActiveFilterKeys(filters).length === 0
                ? unfilteredData
                : unfilteredData.filter(row => filterRow(row, filters));
            setData(filteredData.length === 0 ? null : filteredData);
        }
        setLoading(false);
    }

    function calculateMetricCards(data) {
        if (data) {
            const metrics = data.reduce((acc, row) => {
                acc.totalPaid += row.PaymentAmount ?? 0;
                acc.paidCount += 1;
                acc.numNsf += row.InsufficientFunds === true ? 1 : 0;
                acc.numRecurring += row.RecurringPayment === true ? 1 : 0;
                return acc;
            }, { totalPaid: 0, paidCount: 0, numNsf: 0, numRecurring: 0 });

            return {
                "TotalAmountPaid": metrics.totalPaid,
                "TotalPaidCount": metrics.paidCount,
                "PercentNSF": metrics.paidCount > 0 ? (metrics.numNsf / metrics.paidCount) : null,
                "PercentRecurringPayment": metrics.paidCount > 0 ? (metrics.numRecurring / metrics.paidCount) : null,
                "AverageAmountPaid": metrics.paidCount > 0 ? (metrics.totalPaid / metrics.paidCount) : null
            }
        }
    }

    function renderContent() {
        if (loading) {
            return <Loader type={Types.Inline} />;
        }

        if (!data || data.length === 0) {
            return <NoData />;
        }

        return (
            <div className="table-container">
                <table id="Sweeps_Table" className="table">
                    <thead>
                        <tr key="headers">
                            <th data-sort-method="none">Date Payment Applied</th>
                            <th className="sort-header">Agency G-Code + Name</th>
                            <th className="sort-header">Agent I-Code + Name</th>
                            <th className="sort-header">Line of Business</th>
                            <th className="sort-header">Policy Number</th>
                            <th className="sort-header">Payment Amount</th>
                            <th className="sort-header">Insured's Name</th>
                            <th className="sort-header">Transaction Status</th>
                            <th className="sort-header">Returned Payment</th>
                            <th className="sort-header">Returned Payment Type</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr key={item.PolicyNumber ? `${item.PolicyNumber}-${index}` : `row-${index}`}>
                                <td>{item.PaymentAppliedDate === null ? "" : new Date(item.PaymentAppliedDate).toLocaleDateString()}</td>
                                <td>{`${item.AgencyId} - ${item.AgencyName}`}</td>
                                <td>{`${item.AgentId} - ${item.AgentName}`}</td>
                                <td>{item.LineOfBusiness}</td>
                                <td>{item.PolicyNumber}</td>
                                <td>{`$${item.PaymentAmount?.toLocaleString({ minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? null}`}</td>
                                <td>{item.InsuredName}</td>
                                <td>{item.TransactionStatus}</td>
                                <td>{item.ReturnedPayment ? "Yes" : "No"}</td>
                                <td>{item.ReturnedPaymentType}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return <>
        <ReportHeaderSection
            title="Sweeps Payment Banking"
            user={user}
            substituteUser={substituteAgentInfo}
            data={data}
            setData={setData}
            dataLastUpdatedDate={dataLastUpdatedDate}
            dataStartDate={dataStartDate}
        />
        <ReportSection
            cards={calculateMetricCards(data)}
            sweepsDateFilters
            additionalFilters={sweepsFilters}
            applyFilters={applyFilters}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            filterOptions={filterOptions}
            dataStartDate={sweepsDateBounds.startDate}
            dataLastUpdatedDate={sweepsDateBounds.endDate}
        >
            {renderContent()}

        </ReportSection >
    </>;
}

ReportSweepsPage.propTypes = {
    user: PropTypes.object,
    substituteAgentInfo: PropTypes.shape({
        id: PropTypes.string
    }),
    data: PropTypes.array,
    setData: PropTypes.func.isRequired,
    dataLastUpdatedDate: PropTypes.instanceOf(Date),
    dataStartDate: PropTypes.instanceOf(Date),
    filterOptions: PropTypes.object,
    selectedDateRange: PropTypes.string,
    setSelectedDateRange: PropTypes.func,
    appliedFilters: PropTypes.object,
    setAppliedFilters: PropTypes.func,
};